// ============================================================================
// Service CRUD API - Core service operations, company types, statuses
// ============================================================================
import { supabase } from 'app/auth/auth.js';

// ============================================================================
// Service CRUD
// ============================================================================

/**
 * Get all services
 * @returns {Promise<{data: array|null, error: Error|null}>}
 */
export async function getServices() {
    try {
        const { data, error } = await supabase
            .from('services')
            .select(`
                *,
                service_company_types (
                    company_type_id
                )
            `)
            .order('name');
        if (error) throw error;
        if (data) {
            data.forEach(service => {
                service.company_types_count = service.service_company_types?.length || 0;
            });
        }
        return { data: data || [], error: null };
    } catch (error) {
        console.error('[serviceCrudApi] Failed to get services:', error);
        return { data: null, error };
    }
}

/**
 * Create a new service
 * @param {object} serviceData - Service data
 * @returns {Promise<{data: object|null, error: Error|null}>}
 */
export async function createService(serviceData) {
    try {
        const { data, error } = await supabase
            .from('services')
            .insert(serviceData)
            .select()
            .single();
        if (error) {
            console.error('[serviceCrudApi] Supabase error creating service:', {
                message: error.message,
                details: error.details,
                hint: error.hint,
                code: error.code
            });
            throw error;
        }
        return { data, error: null };
    } catch (error) {
        console.error('[serviceCrudApi] Failed to create service:', error);
        console.error('[serviceCrudApi] Service data that failed:', serviceData);
        return { data: null, error };
    }
}

/**
 * Update a service
 * @param {string} serviceId - Service ID
 * @param {object} updates - Update data
 * @returns {Promise<{data: object|null, error: Error|null}>}
 */
export async function updateService(serviceId, updates) {
    try {
        const { data, error } = await supabase
            .from('services')
            .update(updates)
            .eq('id', serviceId)
            .select()
            .single();
        if (error) throw error;
        return { data, error: null };
    } catch (error) {
        console.error('[serviceCrudApi] Failed to update service:', error);
        return { data: null, error };
    }
}

/**
 * Delete a service
 * @param {string} serviceId - Service ID
 * @returns {Promise<{data: object|null, error: Error|null}>}
 */
export async function deleteService(serviceId) {
    try {
        const { error } = await supabase
            .from('services')
            .delete()
            .eq('id', serviceId);
        if (error) throw error;
        return { data: { id: serviceId }, error: null };
    } catch (error) {
        console.error('[serviceCrudApi] Failed to delete service:', error);
        return { data: null, error };
    }
}

// ============================================================================
// Company Types
// ============================================================================

/**
 * Get all company types
 * @returns {Promise<{data: array|null, error: Error|null}>}
 */
export async function getCompanyTypes() {
    try {
        const { data, error } = await supabase
            .from('company_types')
            .select('*')
            .eq('is_active', true)
            .order('label');
        if (error) throw error;
        const mappedData = data?.map(type => ({
            ...type,
            name: type.label
        })) || [];
        return { data: mappedData, error: null };
    } catch (error) {
        console.error('[serviceCrudApi] Failed to get company types:', error);
        return { data: null, error };
    }
}

/**
 * Get company types for a service
 * @param {string} serviceId - Service ID
 * @returns {Promise<{data: array|null, error: Error|null}>}
 */
export async function getServiceCompanyTypes(serviceId) {
    try {
        const { data, error } = await supabase
            .from('service_company_types')
            .select('company_type_id')
            .eq('service_id', serviceId);
        if (error) throw error;
        const companyTypeIds = data?.map(item => item.company_type_id) || [];
        return { data: companyTypeIds, error: null };
    } catch (error) {
        console.error('[serviceCrudApi] Failed to get service company types:', error);
        return { data: null, error };
    }
}

/**
 * Update company types for a service
 * @param {string} serviceId - Service ID
 * @param {array} companyTypeIds - Array of company type IDs
 * @returns {Promise<{data: array|null, error: Error|null}>}
 */
export async function updateServiceCompanyTypes(serviceId, companyTypeIds) {
    try {
        const { error: deleteError } = await supabase
            .from('service_company_types')
            .delete()
            .eq('service_id', serviceId);
        if (deleteError) throw deleteError;
        if (companyTypeIds.length > 0) {
            const associations = companyTypeIds.map(typeId => ({
                service_id: serviceId,
                company_type_id: typeId
            }));
            const { data, error } = await supabase
                .from('service_company_types')
                .insert(associations)
                .select();
            if (error) throw error;
            return { data, error: null };
        }
        return { data: [], error: null };
    } catch (error) {
        console.error('[serviceCrudApi] Failed to update service company types:', error);
        return { data: null, error };
    }
}

/**
 * Get all services available for a client's company type
 * @param {number} companyTypeId - The company type ID
 * @returns {Promise<{data: array|null, error: Error|null}>}
 */
export async function getServicesForCompanyType(companyTypeId) {
    try {
        const { data, error } = await supabase
            .from('service_company_types')
            .select(`
                service_id,
                services!inner (
                    id,
                    code,
                    name,
                    opt_in,
                    default_assignee_required,
                    is_recurring,
                    recurrence_months,
                    due_date_rule
                )
            `)
            .eq('company_type_id', companyTypeId);
        if (error) throw error;
        const services = data?.map(item => item.services).filter(Boolean) || [];
        return { data: services, error: null };
    } catch (error) {
        console.error('[serviceCrudApi] Failed to get services for company type:', error);
        return { data: null, error };
    }
}

// ============================================================================
// Statuses
// ============================================================================

/**
 * Get all statuses
 * @returns {Promise<{data: array|null, error: Error|null}>}
 */
export async function getAllStatuses() {
    try {
        const { data, error } = await supabase
            .from('service_workflow_states')
            .select('*')
            .eq('is_active', true)
            .order('sort_order');
        if (error) throw error;
        const statuses = data?.map(state => ({
            id: state.id,
            name: state.display_name,
            color: state.color_hex,
            categories: Array.isArray(state.categories) ? state.categories : [state.categories || 'ACTIVE_WORK'],
            sort_order: state.sort_order
        })) || [];
        return { data: statuses, error: null };
    } catch (error) {
        console.error('[serviceCrudApi] Failed to get workflow states:', error);
        return { data: null, error };
    }
}
